import React, { Dispatch, Fragment, SetStateAction, useEffect, useMemo, useState } from 'react'

// Third party libraries
import { produce } from 'immer'

// ** Type Imports
import {
  CheckboxUIConfigType,
  CustomerEligibilityConfigType,
  DiscountFormErrorState,
  DiscountFormStateType,
  EligibilityUIConfigType,
  GenerationType,
  InputUIConfigType,
  UploadCSVUIConfigType
} from 'src/lib/types/discounts'

// ** Util Imports
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { getUIConfig } from 'src/lib/utils/discountHelpers'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import { defaultErrorState } from 'src/assets/DiscountsConfig'

// ** Child Imports
import FormTile from 'src/components/common/containers/FormTile'
import FloRadioGroup from 'src/components/common/form-elements/FloRadioGroup'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { Link, Typography } from '@mui/material'
import { apiURI } from 'src/lib/utils/constants'
import { getValueInRange, truncateString } from 'src/lib/utils/helpers'
import TextFieldError from 'src/components/common/form-elements/TextFieldError'
import FileUploaderWithActions from 'src/components/common/form-elements/FileUploaderWithActions'
import { CSVPreview } from 'src/components/common/ui-preview/CSVPreview'
import { Info, Users } from 'react-feather'
import { ErrorType } from 'src/lib/hooks/useCSVValidator'
import MultiSelectInput from 'src/components/common/form-elements/MultiSelectInput'
import useSWR from 'swr'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import UTMSelector from 'src/components/common/flow-elements/cart-conditions/UTMSelector'
import { UTMSelectorStateType } from 'src/lib/types/common/customerConditions'
import { SegmentDropDownItemType } from 'src/lib/types/segment'
import { AddressBook, PingPong, Tag } from '@phosphor-icons/react'
interface CustomerEligibilityFormProps {
  uiConfig: EligibilityUIConfigType
  setFormState: Dispatch<SetStateAction<DiscountFormStateType | undefined>>
  formState: CustomerEligibilityConfigType | undefined
  isEditMode: boolean
  generationType: GenerationType | undefined
  formErrors: DiscountFormErrorState
  setFormErrors: Dispatch<SetStateAction<DiscountFormErrorState>>
}

const CustomerEligibilityForm: React.FC<CustomerEligibilityFormProps> = ({
  uiConfig,
  formState,
  setFormState,
  formErrors,
  setFormErrors
}) => {
  const {
    valueType,
    minOrderCount,
    maxOrderCount,
    maxOrderCountCheckboxConfig,
    segmentIds,
    utmState = []
  } = formState || {}

  const { customerEligibilityUIConfig } = uiConfig || {}

  const {
    getters: { merchant }
  } = useMerchant()
  const { accountId } = merchant || {}

  const {
    data: segmentsResponse,
    error,
    isValidating
  } = useSWR(
    merchant?.accountId
      ? [`/merchant/${merchant?.accountId}/segments?offset=1&limit=100`, apiURI.SEGMENTS_ENGINE]
      : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const segmentList: any[] = useMemo(() => {
    if (isValidating || error) {
      return []
    }
    const segments: SegmentDropDownItemType[] = (segmentsResponse as any)?.data?.data?.map((segment: any) => ({
      value: segment?.id,
      label: segment?.name,
      type:
        segment?.type === 'SORTMENT_AUDIENCE'
          ? 'DYNAMIC'
          : segment?.type === 'CUSTOMER_TAG'
            ? 'CUSTOMER_TAG'
            : 'STATIC'
    }))
    return segments
  }, [segmentsResponse, isValidating, error])

  const uploadCSVUIConfig: UploadCSVUIConfigType = getUIConfig(
    customerEligibilityUIConfig?.uploadCSVUIConfig,
    formState
  )
  const minOrderCountInputUIConfig: InputUIConfigType = getUIConfig(
    customerEligibilityUIConfig?.minOrderCountInput,
    formState
  )
  const maxOrderCountCheckboxUIConfig: CheckboxUIConfigType = getUIConfig(
    customerEligibilityUIConfig?.maxOrderCountCheckbox,
    formState
  )
  const maxOrderCountInputUIConfig: InputUIConfigType = getUIConfig(
    customerEligibilityUIConfig?.maxOrderCountInput,
    formState?.maxOrderCountCheckboxConfig
  )

  const renderEligibilityTileSection = () => {
    switch (valueType) {
      case 'UTM_PARAMETERS':
        return (
          <UTMSelector
            state={utmState}
            setState={(utm: UTMSelectorStateType[]) => {
              setFormState(
                produce((draft: any) => {
                  draft.customerEligibility.utmState = utm
                })
              )
            }}
            errorState={formErrors.utmState}
            setErrorState={error => setFormErrors({ ...formErrors, utmState: error })}
          />
        )

      case 'CONDITIONAL':
        return renderOrderCountConditionSection()

      case 'SPECIFIC':
        return renderCSVUploadSection()

      case 'SEGMENTS':
        return renderSegmentsSection()
    }
  }

  const renderEligibilityTile = () => {
    return (
      <FormTile title={customerEligibilityUIConfig?.title}>
        <div className='flex flex-col w-full gap-3'>
          <FloRadioGroup
            selectedOption={valueType}
            options={customerEligibilityUIConfig?.eligibilityRadioGroup?.radioItemsUIConfig}
            groupName={'eligibility-radio'}
            setSelectedOption={(selectedOption: any) => {
              setFormState(
                produce((draft: any) => {
                  draft.customerEligibility.valueType = selectedOption
                  if (selectedOption !== 'SEGMENTS') {
                    draft.customerEligibility.segmentIds = []
                  }
                })
              )
            }}
          />
          {renderEligibilityTileSection()}
        </div>
      </FormTile>
    )
  }

  const renderOrderCountConditionSection = () => {
    return (
      <div className='flex flex-col w-full gap-3'>
        <div className='flex w-1/2 gap-2'>
          <div className='w-1/2'>
            <InputTextField
              textFieldId='min-order-count-input'
              label={minOrderCountInputUIConfig?.label}
              inputValue={minOrderCount}
              inputProps={{ min: 0, max: 999999 }}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFormState(
                  produce((draft: any) => {
                    draft.customerEligibility.minOrderCount = getValueInRange(event.target.value, 999999, 0, true)
                  })
                )
              }}
              inputType={'number'}
              inputModeType={'tel'}
              errorStatus={formErrors?.minOrderCount?.status}
              errorMessage={errorMappings?.[formErrors?.minOrderCount?.errorCode as keyof typeof errorMappings]}
              handleFocus={() => setFormErrors({ ...formErrors, minOrderCount: defaultErrorState })}
            />
          </div>
          <div className={`w-1/2`}>
            <div className={`flex ${maxOrderCountInputUIConfig?.isHidden ? 'hidden' : ''}`}>
              <InputTextField
                textFieldId='max-order-count-input'
                label={maxOrderCountInputUIConfig?.label}
                inputValue={maxOrderCount}
                inputProps={{ min: 1, max: 999999 }}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFormState(
                    produce((draft: any) => {
                      draft.customerEligibility.maxOrderCount = getValueInRange(event.target.value, 999999, 1, true)
                    })
                  )
                }}
                inputType={'number'}
                inputModeType={'tel'}
                errorStatus={formErrors?.maxOrderCount?.status}
                errorMessage={errorMappings?.[formErrors?.maxOrderCount?.errorCode as keyof typeof errorMappings]}
                handleFocus={() => setFormErrors({ ...formErrors, maxOrderCount: defaultErrorState })}
              />
            </div>
          </div>
        </div>
        <div className={`${maxOrderCountCheckboxUIConfig?.isHidden ? 'hidden' : ''}`}>
          <FloCheckbox
            value={Boolean(maxOrderCountCheckboxConfig?.valueType)}
            label={maxOrderCountCheckboxUIConfig?.label}
            setValue={(checked: boolean) => {
              setFormState(
                produce((draft: any) => {
                  draft.customerEligibility.maxOrderCountCheckboxConfig.valueType = checked
                })
              )
            }}
          />
        </div>
      </div>
    )
  }

  const renderCSVUploadSection = () => {
    return (
      <div className={`${uploadCSVUIConfig?.isHidden ? 'hidden' : ''}`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.header' }}>
            Sample CSV file format:
          </Typography>
          <CSVPreview rows={[['91877887872'], ['91877887873']]} columns={['91877887874']} />
          <FileUploaderWithActions
            buttonText={uploadCSVUIConfig?.uploadButton?.text ?? ''}
            type='.csv'
            uploadLink='/attributes/v1/files/upload'
            size='medium'
            variant='outlined'
            color='primary'
            acceptedSize={104857600}
            disabled={false}
            prevUploadedFileURL={formState?.fileMeta?.downloadUrl}
            prevUploadedFileName={formState?.fileMeta?.fileKey}
            prevUploadedFileSize={formState?.fileMeta?.size}
            setUploadedFileData={fileData => {
              setFormState(
                produce((draft: any) => {
                  draft.customerEligibility.uploadedFileFormData = fileData
                  draft.customerEligibility.uploadedFilePath = fileData.url
                })
              )
            }}
            errorsToCheck={[ErrorType.FILE_SIZE_EXCEEDED, ErrorType.CUSTOMER_PHONE_NUMBERS]}
          />
        </div>
        <div className=' bg-[#C7981F1A] flex flex-row justify-center items-center gap-1 w-fit p-2 rounded-lg border-yellow-400 border-1 mt-2'>
          <Info size={16} className='pr-1' color='#C7981F' />
          <Typography variant='caption'>{uploadCSVUIConfig?.infoText}</Typography>
          <Link
            href={uploadCSVUIConfig?.sampleDownloadLink?.link}
            target='_blank'
            sx={{
              cursor: 'pointer',
              color: 'text.primary',
              fontSize: 13,
              textDecoration: 'underline',
              fontWeight: 500
            }}
          >
            {uploadCSVUIConfig?.sampleDownloadLink?.text}
          </Link>
        </div>
        {formErrors?.customerCSV?.status ? (
          <TextFieldError message={errorMappings?.[formErrors?.customerCSV?.errorCode as keyof typeof errorMappings]} />
        ) : (
          <></>
        )}
      </div>
    )
  }

  const renderSegmentsSection = () => {
    return (
      <div>
        <MultiSelectInput
          id='column-value-select'
          label=''
          values={segmentIds || []}
          setValues={(value: any) => {
            setFormState(
              produce((draft: any) => {
                draft.customerEligibility.segmentIds = value
              })
            )
          }}
          options={segmentList}
          placeHolderText='Search'
          allowNoSelection={true}
          renderOption={(props: any, option: SegmentDropDownItemType) => {
            return (
              <li {...props}>
                <div className='flex justify-between w-full items-center'>
                  <div className='flex justify-start items-center space-x-2'>
                    {option?.type === 'STATIC' ? (
                      <AddressBook className='w-4 h-4' />
                    ) : option?.type === 'CUSTOMER_TAG' ? (
                      <Tag className='w-4 h-4' />
                    ) : (
                      <PingPong className='w-4 h-4' />
                    )}
                    <p>{truncateString(option?.label, 32)}</p>
                  </div>
                </div>
              </li>
            )
          }}
          renderTag={(option: SegmentDropDownItemType) => {
            return (
              <div className='flex justify-between space-x-2 items-center'>
                <div className='flex justify-start items-center space-x-2'>
                  {option?.type === 'STATIC' ? (
                      <AddressBook className='w-4 h-4' />
                    ) : option?.type === 'CUSTOMER_TAG' ? (
                      <Tag className='w-4 h-4' />
                    ) : (
                      <PingPong className='w-4 h-4' />
                    )}
                  <p>{truncateString(option?.label, 24)}</p>
                </div>
              </div>
            )
          }}
        />
        {formErrors?.customerSegments?.status ? (
          <TextFieldError
            message={errorMappings?.[formErrors?.customerSegments?.errorCode as keyof typeof errorMappings]}
          />
        ) : (
          <></>
        )}
      </div>
    )
  }

  return (
    <>
      <div id='application' className={`${customerEligibilityUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderEligibilityTile()}
      </div>
    </>
  )
}

export default CustomerEligibilityForm
